import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Instagram, MapPin, Scissors, LogOut, User, Shield, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useClientAuth } from '@/context/ClientAuthContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated: clientAuth, client, logout: clientLogout, getAuthHeaders: getClientHeaders } = useClientAuth();
  const { isAuthenticated: adminAuth, logout: adminLogout } = useAuth();

  const navLinks = [
    { name: 'HOME', path: '/' },
    { name: 'PRENOTA', path: '/prenota' },
    { name: 'STILI', path: '/stili' },
    { name: 'GALLERIA', path: '/galleria' },
    { name: 'IL SALONE', path: '/salone' },
    { name: 'FEDELTÀ', path: '/fedelta' },
    { name: 'PROFILO', path: '/profilo' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    if (adminAuth) {
      adminLogout();
    }
    if (clientAuth) {
      clientLogout();
    }
    toast.success('Logout effettuato');
    navigate('/login');
  };

  useEffect(() => {
    if (clientAuth && client) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [clientAuth, client]);

  const fetchNotifications = async () => {
    try {
      const headers = getClientHeaders();
      const response = await axios.get(`${API}/notifications/my-notifications`, { headers });
      setNotifications(response.data);
    } catch (error) { console.error('Failed to fetch notifications:', error); }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) { console.error('Failed to mark notification read:', error); }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Close dropdown on click outside
  useEffect(() => {
    if (!notifOpen) return;
    const handleClick = (e) => {
      if (!e.target.closest('[data-testid="notif-dropdown"]') && !e.target.closest('[data-testid="desktop-notif-bell"]')) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [notifOpen]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 min-w-0" aria-label="Capelli&Capelli - Sala Maschile di Evian Ruta" data-testid="header-logo-link">
              <img src="/logo-header.png" alt="" className="h-12 w-12 sm:h-14 sm:w-14 rounded-md object-contain flex-shrink-0" data-testid="header-logo" />
              <div className="leading-tight min-w-0" data-testid="header-salon-name">
                <p className="text-base sm:text-xl font-extrabold text-foreground truncate" style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '0.03em' }}>Capelli&amp;Capelli</p>
                <p className="text-[10px] sm:text-xs text-accent font-semibold uppercase tracking-[0.18em] truncate">Sala Maschile di Evian Ruta</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-accent ${
                    isActive(link.path) ? 'text-accent' : 'text-foreground/80'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {adminAuth && (
                <Link
                  to="/admin"
                  className={`text-sm font-medium transition-colors hover:text-accent flex items-center gap-1 ${
                    location.pathname.startsWith('/admin') ? 'text-accent' : 'text-foreground/80'
                  }`}
                  data-testid="desktop-admin-link"
                >
                  <Shield className="h-4 w-4" />
                  Admin
                </Link>
              )}
              
              {(clientAuth || adminAuth) && (
                <div className="flex items-center gap-3 ml-4 pl-4 border-l border-border">
                  {/* Notification Bell */}
                  {clientAuth && (
                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setNotifOpen(!notifOpen)}
                        className="relative"
                        data-testid="desktop-notif-bell"
                      >
                        <Bell className="h-4 w-4" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-accent text-[10px] font-bold flex items-center justify-center text-accent-foreground">
                            {unreadCount}
                          </span>
                        )}
                      </Button>
                      {notifOpen && (
                        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-card border border-border rounded-xl shadow-2xl z-50" data-testid="notif-dropdown">
                          <div className="p-3 border-b border-border flex items-center justify-between">
                            <h4 className="font-semibold text-sm">Notifiche</h4>
                            {unreadCount > 0 && <span className="text-xs text-accent">{unreadCount} nuove</span>}
                          </div>
                          {notifications.length === 0 ? (
                            <div className="p-6 text-center text-sm text-muted-foreground">Nessuna notifica</div>
                          ) : (
                            notifications.slice(0, 10).map((notif) => (
                              <div
                                key={notif.id}
                                onClick={() => markAsRead(notif.id)}
                                className={`p-3 border-b border-border/50 cursor-pointer hover:bg-secondary/50 transition-colors ${!notif.read ? 'bg-accent/5' : ''}`}
                                data-testid={`notif-item-${notif.id}`}
                              >
                                <p className="text-sm">{notif.message}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {new Date(notif.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </p>
                                {!notif.read && <span className="inline-block w-2 h-2 rounded-full bg-accent mt-1" />}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-accent" />
                    <span className="font-medium">{client?.first_name || 'Admin'}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="hover:bg-destructive/10 hover:text-destructive"
                    data-testid="desktop-logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button - Sheet */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Scissors className="h-5 w-5 text-accent" />
                    Menu
                  </SheetTitle>
                </SheetHeader>
                
                <nav className="mt-6 flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        isActive(link.path) 
                          ? 'bg-accent text-accent-foreground' 
                          : 'text-foreground/80 hover:bg-accent/10'
                      }`}
                    >
                      {link.name}
                    </Link>
                  ))}
                  
                  {/* Admin Section - Only show if admin is authenticated */}
                  {adminAuth && (
                    <>
                      <div className="my-4 border-t border-border" />
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                          isActive('/admin')
                            ? 'bg-accent text-accent-foreground'
                            : 'text-foreground/80 hover:bg-accent/10'
                        }`}
                      >
                        <Shield className="h-4 w-4" />
                        Admin
                      </Link>
                    </>
                  )}
                  
                  {(clientAuth || adminAuth) && (
                    <>
                      <div className="my-4 border-t border-border" />
                      {clientAuth && (
                        <Link
                          to="/profilo"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-4 py-3 rounded-lg text-sm font-medium transition-colors text-foreground/80 hover:bg-accent/10 flex items-center justify-between"
                          data-testid="mobile-notif-link"
                        >
                          <span className="flex items-center gap-2">
                            <Bell className="h-4 w-4" />
                            Notifiche
                          </span>
                          {unreadCount > 0 && (
                            <span className="w-5 h-5 rounded-full bg-accent text-xs font-bold flex items-center justify-center text-accent-foreground">
                              {unreadCount}
                            </span>
                          )}
                        </Link>
                      )}
                      <div className="px-4 py-2 text-sm text-muted-foreground">
                        Connesso come: <span className="font-semibold text-foreground">{client?.first_name || 'Admin'}</span>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="mx-4 justify-start hover:bg-destructive/10 hover:text-destructive"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </Button>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-secondary border-t border-border mt-auto">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4" data-testid="footer-brand">
                <img src="/logo-header.png" alt="" className="h-14 w-14 rounded-md object-contain" />
                <div className="leading-tight">
                  <h3 className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Capelli&amp;Capelli</h3>
                  <p className="text-xs text-accent font-semibold uppercase tracking-[0.18em]">Sala Maschile di Evian Ruta</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Stile, precisione e personalità</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-foreground">CONTATTI</h4>
              <div className="space-y-3">
                <a href="tel:" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors">
                  <Phone className="h-4 w-4" />
                  <span>Chiama per Prenotare</span>
                </a>
                <a href="#" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors">
                  <Instagram className="h-4 w-4" />
                  <span>@capelliecapelli</span>
                </a>
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5" />
                  <span>Indirizzo da configurare</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-foreground">ORARI</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Lunedì: Chiuso</p>
                <p>Mar - Sab: 9:00 - 19:00</p>
                <p>Domenica: Chiuso</p>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Capelli&amp;Capelli - Sala Maschile di Evian Ruta - Tutti i diritti riservati</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
